/**
 * ScriptedDriver — replays a `TableScript` (tutorial, pure-client specs, demo autoplay) through
 * the TableDriver seam. No network.
 *
 * Step semantics, in order: `coach` is emitted on entering the step; `await` pauses until the
 * app sends a matching intent (or `next()` is called); `delayMs` waits; then `state` (deep
 * patch over the current view), `private` and `cue` are emitted. With `autoplay:false` each
 * step runs only on `next()`.
 *
 * Two things exist for the gesture-gated tutorial (`tutorial/Tutorial.tsx`):
 *
 * - **`onRefused`** fires when an intent arrives while the script is waiting for a *different*
 *   one. The step stays parked — a wrong card never advances anything — but the UI now has
 *   somewhere to hang the nudge ("Try the 7♣") instead of the player getting silence.
 * - **`delayMs` is interruptible.** `next()` during a dwell cuts it short rather than waiting out
 *   a delay nobody is reading any more, which is what makes a "Next" button on an informational
 *   beat do something visible.
 */
import type { ConnectOptions, DriverConnection, TableDriver } from './TableDriver';
import {
  type Cue,
  type IntentName,
  type Intents,
  type LooseCue,
  type SeatView,
  type TableView,
  emptySeatView,
  emptyTableView,
  toCue,
} from './types-local';

type DeepPartial<T> = { [K in keyof T]?: T[K] extends (infer U)[] ? DeepPartial<U>[] : T[K] extends object ? DeepPartial<T[K]> : T[K] };

/** A range/set test on one payload value; every listed bound must hold. */
export interface ScriptWhere {
  min?: number;
  max?: number;
  oneOf?: readonly unknown[];
}

export interface ScriptAwait {
  intent: IntentName;
  /** Keys that must deep-equal the sent payload's keys. */
  match?: Record<string, unknown>;
  /**
   * Looser constraints for beats that accept a range rather than one answer, keyed by dotted path
   * into the payload (`"bid.n": { min: 2 }` accepts any tricks bid of 2 or more, and rejects nil,
   * which has no `n`). Checked in addition to `match`. JSON cannot hold a predicate function, and
   * the tutorial needs "accept ≥2" (DESIGN-client §6 beat 2).
   */
  where?: Record<string, ScriptWhere>;
}

export interface ScriptStep {
  label?: string;
  state?: DeepPartial<TableView>;
  private?: Partial<SeatView>;
  cue?: LooseCue;
  await?: ScriptAwait;
  coach?: string;
  delayMs?: number;
  /**
   * Which coach-mark anchor this beat points at (`presentation.anchors[key]`, DESIGN-client §6:
   * `hand.card:BJ`, `bid.stack`, `shelf.0.bracket`, `trick.center`, `seat.2`). Passed through to
   * `onCoach` on the step; the driver itself does not read it.
   */
  anchor?: string;
  /**
   * Extra coach copy the tutorial UI shows on a condition rather than on entering the step, keyed
   * by that condition (beat 2's `bid:0` nil explainer). Passed through; the driver ignores it.
   */
  hints?: Record<string, string>;
}

export interface TableScript {
  seed?: number | string;
  /** Starting view; defaults to an empty lobby table. */
  initial?: DeepPartial<TableView>;
  steps: ScriptStep[];
}

export interface ScriptedDriverOptions {
  autoplay?: boolean;
  /** Multiply every `delayMs` (0 = skip delays; specs use this). */
  timeScale?: number;
  sleep?: (ms: number) => Promise<void>;
}

export interface SentIntent {
  intent: IntentName;
  payload: unknown;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  if (isObject(a) && isObject(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
}

/** Deep patch: objects merge, arrays merge index-wise (`trick` is replaced), scalars assign. */
export function mergeView(base: TableView, patch: DeepPartial<TableView> | undefined): TableView {
  if (!patch) return base;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    const cur = out[k];
    if (k === 'trick' || k === 'rematchVotes') out[k] = Array.isArray(v) ? v.map((x) => ({ ...(x as object) })) : { ...(v as object) };
    else if (Array.isArray(v) && Array.isArray(cur)) {
      const len = Math.max(v.length, cur.length);
      out[k] = Array.from({ length: len }, (_, i) => {
        if (i >= v.length) return cur[i];
        const item: unknown = v[i];
        return isObject(item) && isObject(cur[i]) ? { ...(cur[i] as object), ...item } : item;
      });
    } else if (isObject(v) && isObject(cur)) out[k] = { ...cur, ...v };
    else out[k] = v;
  }
  return out as unknown as TableView;
}

/** Walk a dotted path into a payload; undefined when any hop is missing. */
function at(payload: unknown, path: string): unknown {
  let cur: unknown = payload;
  for (const key of path.split('.')) {
    if (!isObject(cur)) return undefined;
    cur = cur[key];
  }
  return cur;
}

function satisfies(value: unknown, w: ScriptWhere): boolean {
  if (w.oneOf && !w.oneOf.some((x) => deepEqual(x, value))) return false;
  if (w.min !== undefined || w.max !== undefined) {
    if (typeof value !== 'number' || Number.isNaN(value)) return false;
    if (w.min !== undefined && value < w.min) return false;
    if (w.max !== undefined && value > w.max) return false;
  }
  return true;
}

export function matchesAwait(step: ScriptAwait, intent: IntentName, payload: unknown): boolean {
  if (step.intent !== intent) return false;
  if (step.where && !Object.entries(step.where).every(([path, w]) => satisfies(at(payload, path), w))) return false;
  if (!step.match) return true;
  if (!isObject(payload)) return Object.keys(step.match).length === 0;
  return Object.entries(step.match).every(([k, v]) => deepEqual(payload[k], v));
}

export class ScriptedDriver implements TableDriver {
  readonly script: TableScript;
  readonly sent: SentIntent[] = [];
  private view: TableView;
  /** Last private view; `private` steps patch over it (sparse patches keep the hand). */
  private priv: SeatView;
  private seq = 0;
  private index = 0;
  private running = false;
  private stopped = false;
  private waiting: { step: ScriptAwait; resolve: () => void } | null = null;
  private stepWaiters: Array<() => void> = [];
  /** Resolvers that cut a `delayMs` dwell short (see `dwell`). */
  private dwellWaiters: Array<() => void> = [];
  private doneResolve!: () => void;
  readonly done: Promise<void>;
  private readonly autoplay: boolean;
  private readonly timeScale: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly stateCbs = new Set<(v: TableView) => void>();
  private readonly cueCbs = new Set<(c: Cue) => void>();
  private readonly privateCbs = new Set<(v: SeatView) => void>();
  private readonly connCbs = new Set<(s: DriverConnection) => void>();
  private readonly coachCbs = new Set<(text: string | null, step: ScriptStep) => void>();
  private readonly stepCbs = new Set<(index: number, step: ScriptStep) => void>();
  private readonly refusedCbs = new Set<(intent: IntentName, payload: unknown, expected: ScriptAwait) => void>();

  constructor(script: TableScript, opts: ScriptedDriverOptions = {}) {
    this.script = script;
    this.autoplay = opts.autoplay ?? true;
    this.timeScale = opts.timeScale ?? 1;
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.view = mergeView(emptyTableView(), script.initial);
    this.priv = emptySeatView(0);
    this.done = new Promise<void>((r) => (this.doneResolve = r));
  }

  static async load(url: string, fetchFn: typeof fetch = fetch): Promise<TableScript> {
    const res = await fetchFn(url);
    if (!res.ok) throw new Error(`script ${url}: HTTP ${res.status}`);
    return (await res.json()) as TableScript;
  }

  get stepIndex(): number {
    return this.index;
  }

  get currentView(): TableView {
    return this.view;
  }

  get currentPrivate(): SeatView {
    return this.priv;
  }

  /** The intent the script is waiting for, if any. */
  get awaiting(): ScriptAwait | null {
    return this.waiting?.step ?? null;
  }

  get finished(): boolean {
    return this.index >= this.script.steps.length;
  }

  async connect(_opts?: ConnectOptions): Promise<void> {
    this.connCbs.forEach((cb) => cb('connected'));
    this.stateCbs.forEach((cb) => cb(this.view));
    if (this.autoplay) void this.run();
  }

  /**
   * The step the run loop is sitting on, or null once the script is finished. The tutorial reads
   * it to tell an ACTION beat (one with an `await`, which only a gesture may pass) from an
   * informational one.
   */
  get currentStep(): ScriptStep | null {
    return this.script.steps[this.index] ?? null;
  }

  /**
   * Advance: resolves a pending `await` (skipping the intent), cuts a `delayMs` dwell short, or,
   * when not autoplaying, runs the next step. Resolves once that step has been applied.
   */
  async next(): Promise<void> {
    if (this.finished) return;
    if (this.waiting) {
      const applied = this.untilStepApplied();
      this.waiting.resolve();
      this.waiting = null;
      await applied;
      return;
    }
    if (this.running) {
      const applied = this.untilStepApplied();
      this.releaseDwell();
      await applied;
      return;
    }
    await this.run(true);
  }

  private untilStepApplied(): Promise<void> {
    return new Promise((r) => this.stepWaiters.push(r));
  }

  /** Let go of every dwell currently in progress; the run loop carries straight on. */
  private releaseDwell(): void {
    const waiters = this.dwellWaiters;
    this.dwellWaiters = [];
    waiters.forEach((w) => w());
  }

  /**
   * A `delayMs` pause that `next()` (or `leave()`) can cut short.
   *
   * The injected `sleep` is still the timer — specs that count sleeps still see every delay — but
   * it races a resolver anyone holding the driver can trip, so a player who has read the beat is
   * not made to sit out the rest of it.
   */
  private async dwell(ms: number): Promise<void> {
    if (ms <= 0) return;
    let release!: () => void;
    const skipped = new Promise<void>((r) => {
      release = r;
      this.dwellWaiters.push(r);
    });
    try {
      await Promise.race([this.sleep(ms), skipped]);
    } finally {
      const i = this.dwellWaiters.indexOf(release);
      if (i >= 0) this.dwellWaiters.splice(i, 1);
    }
  }

  private async run(single = false): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      while (!this.stopped && this.index < this.script.steps.length) {
        const step = this.script.steps[this.index]!;
        this.coachCbs.forEach((cb) => cb(step.coach ?? null, step));
        if (step.await) {
          await new Promise<void>((resolve) => {
            this.waiting = { step: step.await!, resolve };
          });
          this.waiting = null;
          if (this.stopped) break;
        }
        if (step.delayMs && this.timeScale > 0) await this.dwell(step.delayMs * this.timeScale);
        if (this.stopped) break;
        this.apply(step);
        this.index++;
        this.stepCbs.forEach((cb) => cb(this.index, step));
        const waiters = this.stepWaiters;
        this.stepWaiters = [];
        waiters.forEach((w) => w());
        if (single || !this.autoplay) break;
      }
    } finally {
      this.running = false;
      if (this.finished) this.doneResolve();
    }
  }

  private apply(step: ScriptStep): void {
    if (step.state) {
      this.view = mergeView(this.view, step.state);
      this.stateCbs.forEach((cb) => cb(this.view));
    }
    if (step.private) {
      this.priv = { ...this.priv, ...step.private };
      this.privateCbs.forEach((cb) => cb(this.priv));
    }
    if (step.cue) {
      const cue = toCue(step.cue, this.seq + 1);
      if (cue.seq > this.seq) this.seq = cue.seq;
      this.cueCbs.forEach((cb) => cb(cue));
    }
  }

  send<K extends keyof Intents>(intent: K, payload: Intents[K]): void {
    this.sent.push({ intent, payload });
    if (!this.waiting) return;
    if (matchesAwait(this.waiting.step, intent, payload)) {
      const { resolve } = this.waiting;
      this.waiting = null;
      resolve();
      return;
    }
    // Refused. The beat stays parked — this is the whole point of a gesture gate — but the UI is
    // told, so the coach can say which card it meant instead of the drop reading as a dead click.
    const expected = this.waiting.step;
    this.refusedCbs.forEach((cb) => cb(intent, payload, expected));
  }

  onState(cb: (view: TableView) => void): () => void {
    this.stateCbs.add(cb);
    return () => this.stateCbs.delete(cb);
  }

  onCue(cb: (cue: Cue) => void): () => void {
    this.cueCbs.add(cb);
    return () => this.cueCbs.delete(cb);
  }

  onPrivate(cb: (v: SeatView) => void): () => void {
    this.privateCbs.add(cb);
    return () => this.privateCbs.delete(cb);
  }

  onConnection(cb: (s: DriverConnection) => void): () => void {
    this.connCbs.add(cb);
    return () => this.connCbs.delete(cb);
  }

  /** Coach-mark text for the current step (null clears). */
  onCoach(cb: (text: string | null, step: ScriptStep) => void): () => void {
    this.coachCbs.add(cb);
    return () => this.coachCbs.delete(cb);
  }

  /** Fired after each step is applied with the new index. */
  onStep(cb: (index: number, step: ScriptStep) => void): () => void {
    this.stepCbs.add(cb);
    return () => this.stepCbs.delete(cb);
  }

  /**
   * Fired when an intent arrives that the parked `await` will not take. The script does not move;
   * the callback is the tutorial's cue to nudge.
   */
  onRefused(cb: (intent: IntentName, payload: unknown, expected: ScriptAwait) => void): () => void {
    this.refusedCbs.add(cb);
    return () => this.refusedCbs.delete(cb);
  }

  leave(): void {
    this.stopped = true;
    const w = this.waiting;
    this.waiting = null;
    w?.resolve();
    this.releaseDwell();
    this.connCbs.forEach((cb) => cb('lost'));
  }
}
